import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppAsyncSelect as AsyncSelect } from "../../components/ui/AppReactSelect";
import CategorySuggestions from "../../components/catalog/CategorySuggestions";
import { categoryPickerOptions, groupUnits } from "../../utilities/catalogTaxonomy";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import DOMPurify from "dompurify";
import { useHistory } from "react-router";
import { updateListing, getListing } from "../../actions/listingsActions";
import { uploadImageToCloudinary as sharedUpload } from "../../utilities/imageUpload";
import ImageUploadStyledAlert from "views/Components/ImageUploadStyledAlert";
import { getUserFromLocalStorage } from "utilities/user";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:         "#f4f6fb",
  panel:      "#ffffff",
  header:     "#1a2b4a",
  blue:       "#2c7be5",
  blueSoft:   "#eaf2ff",
  teal:       "#00b69b",
  tealSoft:   "#e6faf7",
  red:        "#e63757",
  redSoft:    "#fff0f3",
  orange:     "#f6961e",
  orangeSoft: "#fff4e6",
  text:       "#1a2b4a",
  textMid:    "#4a5568",
  textLight:  "#8898aa",
  border:     "#e2e8f0",
  inputBg:    "#f8fafd",
  shadow:     "0 2px 12px rgba(26,43,74,0.08)",
  radius:     "10px",
  radiusSm:   "6px",
};

// ── Reusable field label ───────────────────────────────────────────────────────
const FieldLabel = ({ num, children, required }) => (
  <label style={{
    display: "block", marginBottom: 6,
    fontSize: 12, fontWeight: 700, color: C.textMid,
    textTransform: "uppercase", letterSpacing: "0.06em",
  }}>
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 18, height: 18, borderRadius: "50%",
      background: C.blueSoft, color: C.blue,
      fontSize: 10, fontWeight: 800, marginRight: 7, flexShrink: 0,
    }}>{num}</span>
    {children}
    {required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
  </label>
);

// ── Styled text input ──────────────────────────────────────────────────────────
const Input = ({ style, ...props }) => (
  <input
    style={{
      width: "100%", padding: "9px 12px",
      border: `1px solid ${C.border}`, borderRadius: C.radiusSm,
      background: C.inputBg, color: C.text, fontSize: 13,
      outline: "none", transition: "border-color 0.15s",
      boxSizing: "border-box",
      ...style,
    }}
    onFocus={(e) => (e.target.style.borderColor = C.blue)}
    onBlur={(e)  => (e.target.style.borderColor = C.border)}
    {...props}
  />
);

// ── Section card ───────────────────────────────────────────────────────────────
const Section = ({ icon, title, children }) => (
  <div style={{
    background: C.panel, borderRadius: C.radius,
    border: `1px solid ${C.border}`, boxShadow: C.shadow,
    marginBottom: 20, overflow: "hidden",
  }}>
    <div style={{
      padding: "13px 20px", borderBottom: `1px solid ${C.border}`,
      display: "flex", alignItems: "center", gap: 10,
      background: "#fafbfd",
    }}>
      <i className={icon} style={{ color: C.blue, fontSize: 13 }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: C.textMid,
        textTransform: "uppercase", letterSpacing: "0.07em" }}>{title}</span>
    </div>
    <div style={{ padding: "20px 24px" }}>{children}</div>
  </div>
);

const ListingEditForm = ({ match }) => {
  const listings = useSelector((state) => state.listings);
  const { listing, isLoading } = listings;

  const ListingId = match.params.id;

  const user = getUserFromLocalStorage();

  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState([]);

  //Return policy
  const [useDefaultPolicy, setUseDefaultPolicy] = useState(true);
  const [customPolicy, setCustomPolicy] = useState('');

  const [inputValue, setValue] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");

  // Alert
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [postData, setPostData] = useState({
    title: "",
    description: "",
    price: "",
    countInStock: "",
    measurements: "",
    vendorName: "",
    vendorContactNumber: "",
    vendorAddress: "",
    pic: "",
    category: "",
  }); const [image, setImage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    dispatch(getListing(ListingId));
  }, [ListingId]);

  const dispatch = useDispatch();
  const history = useHistory();

  const clear = () => {
    setPostData({
      title: "",
      description: "",
      price: "",
      countInStock: "",
      measurements: "",
      pic: "",
      category: "",
      vendorName: "",
      vendorContactNumber: "",
      vendorAddress: ""
    }),
      // setDescription("");
      setSelectedCategory([]);
    setSelectedUnit([]);
    setCustomPolicy('');
    setImage("");
    setIsUploading(false);
    setUploadDone(false);
    setUploadProgress(0);
  };

  const backToEditTable = () => {
    history.push("/admin/shopCards");
  };

  //PRELOADS THE FORM WITH DATA FROM LISTING
  useEffect(() => {
    //   if(!post?.title) clear();
    let active = true;

    if (listing?._id) {
      setPostData({
        ...listing,
      });
      if (active) {
        setImage(listing?.product_image);
      }
      if (listing?.category?.length > 0) {
        setSelectedCategory(listing?.category);
      }
      if (listing?.unit_id?.unit.length > 0) {
        setSelectedUnit(listing?.unit_id);
      }
    } else {
      clear();
    }
    return () => {
      active = false;
    };
  }, [listing]);

  const fetchImageToUpload = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      setIsUploading(true);

      try {
        const result = await sharedUpload(image, {
          vendorMedia: {
            vendorType: user?.result?.operationMode || "unknown",
            vendorId: user?.result?._id,
            assetPurpose: "product_cover",
            entityType: "listing",
            entityId: ListingId,
            source: "dashboard",
          },
          onProgress: (percent) => setUploadProgress(percent),
        });

        setImage(result.secure_url);
        setIsUploading(false);
        setUploadDone(true);

        const abortController = new AbortController();
        resolve(abortController);
      } catch (error) {
        setIsUploading(false);
        reject(error);
      }
    });
  }, [image, ListingId, user?.result?._id, user?.result?.operationMode]);

  useEffect(() => {
    let active = true;
    let abortControllerPromise;

    if (image?.name) {
      if (active) {
        abortControllerPromise = fetchImageToUpload();
      }
    }

    return () => {
      active = false;
      if (abortControllerPromise) {
        abortControllerPromise.then((abortController) => {
          abortController.abort();
        });
      }
    };
  }, [image?.name, fetchImageToUpload]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (image?.name && !uploadDone) {
      const abortController = await fetchImageToUpload();
      if (abortController && !abortController.signal.aborted) {
        abortController.abort();
      }
    }

    // Sanitize customPolicy text
    const sanitizedCustomPolicy = DOMPurify.sanitize(customPolicy);

    dispatch(
      updateListing(
        listing?._id,
        {
          ...postData,
          pic: image,
          name: user?.result?.name,
          category: selectedCategory,
          unit_id: selectedUnit,
          description: postData.description, // Use the Quill editor content
          useDefaultPolicy: useDefaultPolicy,
          customPolicy: sanitizedCustomPolicy,
        },
        user.result._id,
        // user.result.user_role,
        clear(),
        history.push("/admin/shopCards"),
      )
    );
  };

  //Fetch categories
  // Every category, kept for the suggestions to resolve their picks against.
  const [allCategories, setAllCategories] = useState([]);
  const loadCategory = async (inputValue) => {
    const res = await fetch("/category/get-all");
    const categories = await res.json();
    if (Array.isArray(categories)) setAllCategories(categories);
    // Grouped, "All"/"Random"/inactive left out (unless already chosen),
    // aliases and keywords searchable.
    return categoryPickerOptions(Array.isArray(categories) ? categories : [], {
      input: inputValue,
      selected: selectedCategory,
    });
  };

  // handle selection
  const handleInputChange = (value) => {
    setValue(value);
  };

  // handle Category Selection
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const loadUnit = async (inputValue) => {
    const res = await fetch(`/unit`);
    const units = await res.json();

    // Grouped by family; plural duplicates left out unless this listing uses one.
    const currentId = selectedUnit?._id || selectedUnit || null;
    const groups = groupUnits(Array.isArray(units) ? units : [], { currentId });
    if (!inputValue) {
      return groups;
    }
    const needle = inputValue.toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        options: group.options.filter((unit) => unit.unit.toLowerCase().includes(needle)),
      }))
      .filter((group) => group.options.length);
  };

  // handle Unit Selection
  const handleUnitChange = (value) => {
    setSelectedUnit(value);
  };

  const isJPEG = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target.result;
        const view = new DataView(buffer);
        const isJPEG =
          view.byteLength >= 2 && view.getUint16(0, false) === 0xffd8; // Check if the file starts with a JPEG SOI marker (0xFFD8)
        resolve(isJPEG);
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const isFileJPEG = await isJPEG(file);
    const fileSizeInMB = file.size / (1024 * 1024);

    if (!isFileJPEG) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "Invalid file type. Only JPG, JPEG, and PNG files are allowed."
      );
      setOpenSnackbar(true);
      setUploadedFileName(""); // Clear the uploaded file name
      return;
    }

    if (fileSizeInMB > 1) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "File size exceeds 1 MB. Please choose a smaller image."
      );
      setOpenSnackbar(true);
      setUploadedFileName(""); // Clear the uploaded file name
      return;
    }

    // Continue with the upload process
    setImage(file);
    setUploadedFileName(file.name); // Set the uploaded file name
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  // const handleDescriptionChange = (value) => {
  //   setDescription(value);
  // };

  const [isSaving, setIsSaving] = useState(false);

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ color: [] }, { background: [] }],
      ['clean'],
    ],
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      background: C.inputBg,
      borderColor: state.isFocused ? C.blue : C.border,
      borderRadius: C.radiusSm,
      boxShadow: "none",
      fontSize: 13,
      minHeight: 38,
      "&:hover": { borderColor: C.blue },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      background: state.isSelected ? C.blue : state.isFocused ? C.blueSoft : "#fff",
      color: state.isSelected ? "#fff" : C.text,
    }),
    multiValue: (base) => ({ ...base, background: C.blueSoft, borderRadius: 4 }),
    multiValueLabel: (base) => ({ ...base, color: C.blue, fontSize: 11, fontWeight: 700 }),
    placeholder: (base) => ({ ...base, color: C.textLight, fontSize: 13 }),
  };

  // Preview URL — show new file blob OR existing Cloudinary URL
  const previewUrl = image
    ? (image instanceof File || image?.name) ? URL.createObjectURL(image) : image
    : null;

  const isSubmitDisabled = isSaving || isUploading || !selectedCategory?.length || !selectedUnit;

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 16px 48px" }}>
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <div style={{
            background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
            borderRadius: C.radius, padding: "22px 28px", marginBottom: 24,
            height: 72,
            animation: "shimmer 1.4s infinite",
            backgroundSize: "200% 100%",
          }} />
          {[200, 120, 160, 140].map((h, i) => (
            <div key={i} style={{
              background: C.panel, borderRadius: C.radius, border: `1px solid ${C.border}`,
              marginBottom: 20, height: h,
              backgroundImage: "linear-gradient(90deg,#e8edf5 25%,#f4f6fb 50%,#e8edf5 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite",
            }} />
          ))}
        </div>
        <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 16px 48px" }}>
      <ImageUploadStyledAlert
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        severity={snackbarSeverity}
        message={snackbarMessage}
      />

      <div style={{ maxWidth: 780, margin: "0 auto" }}>

        {/* ── Header ── */}
        <div style={{
          background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
          borderRadius: C.radius, padding: "20px 28px", marginBottom: 24,
          boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 42, height: 42, borderRadius: C.radiusSm, flexShrink: 0,
              background: "rgba(44,123,229,0.2)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fas fa-edit" style={{ color: "#7eb8f7", fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: "-0.3px", marginBottom: 2 }}>
                Edit Listing
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                {listing?.title ? `Editing: ${listing.title}` : "Update product details below"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={backToEditTable}
            style={{
              background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: C.radiusSm, padding: "7px 16px",
              color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <i className="fas fa-arrow-left" style={{ fontSize: 11 }} />
            Back
          </button>
        </div>

        <form autoComplete="off" onSubmit={async (e) => {
          e.preventDefault();
          setIsSaving(true);
          await handleSubmit(e).catch(() => {});
          setIsSaving(false);
        }}>

          {/* ── Section 1: Product Info ── */}
          <Section icon="fas fa-box" title="Product Information">
            <div style={{ marginBottom: 18 }}>
              <FieldLabel num={1} required>Product Name</FieldLabel>
              <Input
                name="title"
                type="text"
                value={postData.title}
                maxLength="40"
                placeholder="e.g. Fresh Avocados (500g)"
                onChange={(e) => setPostData({ ...postData, title: e.target.value })}
              />
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 4, textAlign: "right" }}>
                {postData.title?.length || 0}/40
              </div>
            </div>

            <div>
              <FieldLabel num={2} required>Product Description</FieldLabel>
              <div style={{
                border: `1px solid ${C.border}`, borderRadius: C.radiusSm,
                overflow: "hidden", background: C.inputBg,
              }}>
                <ReactQuill
                  value={postData.description}
                  onChange={(content) => setPostData({ ...postData, description: content })}
                  modules={modules}
                  formats={['bold','italic','underline','strike','blockquote','list','bullet','size','color','background','code-block']}
                  style={{ height: 200 }}
                  placeholder="Enter product description here..."
                />
              </div>
              <div style={{ height: 48 }} />
            </div>
          </Section>

          {/* ── Section 2: Pricing & Stock ── */}
          <Section icon="fas fa-tag" title="Pricing & Stock">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
              <div>
                <FieldLabel num={3} required>Price (UGX)</FieldLabel>
                <Input
                  type="number"
                  value={postData.price}
                  placeholder="0"
                  onChange={(e) => setPostData({ ...postData, price: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel num={4} required>Count In Stock</FieldLabel>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={postData.countInStock}
                  placeholder="0"
                  onChange={(e) => setPostData({ ...postData, countInStock: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <FieldLabel num={5}>Category</FieldLabel>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  value={selectedCategory}
                  getOptionLabel={(e) => e.category}
                  getOptionValue={(e) => e._id}
                  loadOptions={loadCategory}
                  onInputChange={handleInputChange}
                  onChange={handleCategoryChange}
                  isClearable
                  isMulti
                  styles={selectStyles}
                  placeholder="Select category..."
                />
                <CategorySuggestions
                  name={postData.title}
                  unitId={selectedUnit?._id || null}
                  categories={allCategories}
                  selected={selectedCategory}
                  onAdd={(category) => setSelectedCategory((prev) => [...(prev || []), category])}
                />
              </div>
              <div>
                <FieldLabel num={6} required>Measurement Unit</FieldLabel>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  value={selectedUnit}
                  getOptionLabel={(e) => e.unit}
                  getOptionValue={(e) => e._id}
                  loadOptions={loadUnit}
                  onInputChange={handleInputChange}
                  onChange={handleUnitChange}
                  isClearable
                  styles={selectStyles}
                  placeholder="Select unit..."
                />
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <FieldLabel num={7} required>Measurements</FieldLabel>
              <Input
                type="text"
                inputMode="numeric"
                pattern="^\d*(\.\d{0,2})?$"
                value={postData.measurements}
                placeholder="e.g. 500"
                onChange={(e) => setPostData({ ...postData, measurements: e.target.value })}
              />
            </div>
          </Section>

          {/* ── Section 3: Vendor Info ── */}
          <Section icon="fas fa-store" title="Vendor Details">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <FieldLabel num={8}>Vendor Name</FieldLabel>
                <Input
                  type="text"
                  value={postData.vendorName}
                  placeholder="Enter vendor's name"
                  onChange={(e) => setPostData({ ...postData, vendorName: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel num={9}>Contact Number</FieldLabel>
                <Input
                  type="text"
                  value={postData.vendorContactNumber}
                  placeholder="e.g. 0700 000 000"
                  onChange={(e) => setPostData({ ...postData, vendorContactNumber: e.target.value })}
                />
              </div>
            </div>
            <div>
              <FieldLabel num={10}>Physical Address</FieldLabel>
              <Input
                type="text"
                value={postData.vendorAddress}
                placeholder="Enter vendor's address"
                onChange={(e) => setPostData({ ...postData, vendorAddress: e.target.value })}
              />
            </div>
          </Section>

          {/* ── Section 4: Return Policy ── */}
          <Section icon="fas fa-undo-alt" title="Return Policy">
            <label style={{
              display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
              padding: "10px 14px", borderRadius: C.radiusSm,
              background: useDefaultPolicy ? C.tealSoft : C.bg,
              border: `1px solid ${useDefaultPolicy ? "#9de8df" : C.border}`,
              transition: "all 0.15s",
            }}>
              <div style={{
                width: 36, height: 20, borderRadius: 10, position: "relative",
                background: useDefaultPolicy ? C.teal : C.border,
                transition: "background 0.2s", flexShrink: 0,
              }}>
                <div style={{
                  position: "absolute", top: 2,
                  left: useDefaultPolicy ? 18 : 2,
                  width: 16, height: 16, borderRadius: "50%",
                  background: "#fff", transition: "left 0.2s",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                }} />
                <input
                  type="checkbox"
                  id="default-return-policy-checkbox"
                  checked={useDefaultPolicy}
                  onChange={(e) => setUseDefaultPolicy(e.target.checked)}
                  style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Use default return policy</div>
                <div style={{ fontSize: 11, color: C.textLight }}>
                  {useDefaultPolicy ? "Standard Lusuku return policy will apply" : "Custom policy enabled"}
                </div>
              </div>
            </label>

            {!useDefaultPolicy && (
              <div style={{ marginTop: 14 }}>
                <FieldLabel num="✎" required>Custom Return Policy</FieldLabel>
                <textarea
                  rows={5}
                  placeholder="Please provide the custom return policy here..."
                  required
                  value={customPolicy}
                  onChange={(e) => setCustomPolicy(e.target.value)}
                  style={{
                    width: "100%", padding: "10px 12px", boxSizing: "border-box",
                    border: `1px solid ${C.orange}`, borderRadius: C.radiusSm,
                    background: C.orangeSoft, color: C.text, fontSize: 13,
                    resize: "vertical", outline: "none", minHeight: 120,
                  }}
                />
                <div style={{
                  display: "flex", alignItems: "center", gap: 5,
                  marginTop: 5, fontSize: 11, color: C.orange, fontWeight: 600,
                }}>
                  <i className="fas fa-info-circle" style={{ fontSize: 10 }} />
                  This policy will be shown to customers instead of the default.
                </div>
              </div>
            )}
          </Section>

          {/* ── Section 5: Product Image ── */}
          <Section icon="fas fa-image" title="Product Image">
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>

              {/* Preview */}
              <div style={{
                width: 120, height: 120, borderRadius: C.radiusSm, flexShrink: 0,
                border: `2px dashed ${C.border}`, overflow: "hidden",
                background: C.bg, display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <i className="fas fa-image" style={{ fontSize: 32, color: C.textLight }} />
                )}
              </div>

              {/* Upload controls */}
              <div style={{ flex: 1, minWidth: 200 }}>
                <FieldLabel num={11}>Upload Image</FieldLabel>
                <label
                  htmlFor="image-upload"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "9px 18px", borderRadius: C.radiusSm, cursor: "pointer",
                    background: C.blueSoft, border: `1px solid #b3d4f9`,
                    color: C.blue, fontSize: 12, fontWeight: 700,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#d6eafd")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.blueSoft)}
                >
                  <i className="fas fa-cloud-upload-alt" />
                  {uploadedFileName ? "Replace Image" : "Choose Image"}
                </label>
                <input
                  id="image-upload"
                  name="image"
                  type="file"
                  accept=".jpg,.png,.jpeg"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <div style={{ marginTop: 8, fontSize: 11, color: C.textLight }}>
                  JPG / PNG · Max 1 MB
                </div>

                {/* Upload progress bar */}
                {isUploading && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 11, color: C.textMid }}>
                      <span>Uploading…</span>
                      <span style={{ fontWeight: 700 }}>{uploadProgress}%</span>
                    </div>
                    <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{
                        height: "100%", borderRadius: 3, background: C.blue,
                        width: `${uploadProgress}%`, transition: "width 0.3s ease",
                      }} />
                    </div>
                  </div>
                )}

                {uploadDone && !isUploading && (
                  <div style={{
                    marginTop: 10, display: "flex", alignItems: "center", gap: 6,
                    fontSize: 12, color: C.teal, fontWeight: 600,
                  }}>
                    <i className="fas fa-check-circle" />
                    Image uploaded successfully
                  </div>
                )}

                {uploadedFileName && !isUploading && (
                  <div style={{ marginTop: 6, fontSize: 11, color: C.textLight }}>
                    {uploadedFileName}
                  </div>
                )}
              </div>
            </div>
          </Section>

          {/* ── Footer Actions ── */}
          <div style={{
            display: "flex", justifyContent: "flex-end", gap: 10,
            padding: "16px 0 4px",
          }}>
            <button
              type="button"
              onClick={backToEditTable}
              style={{
                padding: "9px 22px", borderRadius: C.radiusSm,
                border: `1px solid ${C.border}`, background: C.panel,
                color: C.textMid, fontSize: 13, fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 7,
              }}
            >
              <i className="fas fa-times" style={{ fontSize: 11 }} />
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              style={{
                padding: "9px 28px", borderRadius: C.radiusSm,
                border: "none",
                background: isSubmitDisabled
                  ? C.border
                  : `linear-gradient(135deg, ${C.blue} 0%, #1a5fc4 100%)`,
                color: isSubmitDisabled ? C.textLight : "#fff",
                fontSize: 13, fontWeight: 700, cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 8,
                boxShadow: isSubmitDisabled ? "none" : "0 2px 8px rgba(44,123,229,0.35)",
                transition: "all 0.15s",
              }}
            >
              {isSaving ? (
                <>
                  <span style={{
                    width: 13, height: 13, border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff", borderRadius: "50%",
                    display: "inline-block", animation: "spin 0.7s linear infinite",
                  }} />
                  Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-save" style={{ fontSize: 12 }} />
                  Save Changes
                </>
              )}
            </button>
          </div>

        </form>
      </div>

      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin    { to { transform: rotate(360deg); } }
        .ql-toolbar { border-color: ${C.border} !important; background: ${C.inputBg} !important; }
        .ql-container { border-color: ${C.border} !important; font-size: 13px !important; }
        .ql-editor { min-height: 160px; }
      `}</style>
    </div>
  );
};

export default ListingEditForm;
